import { pushSyncBooking } from "@/api/booking/booking";
import { LogLevel, LogService } from "@/services/logging";
import {
  IProductSlotBooking,
  IUntypedBooking,
  SyncStatus,
} from "@/types/db/product-slot-booking";
import BookingModule from "@/store/modules/booking";
import { AccountingState, PaymentMethods } from "@/types/db/accounting";
import { SharedCallbacks } from "@/utils/shared-resources";
import { cloneDeep } from "lodash";
import { syncAvailabilities } from "./product-slot-booking";
import dayjs from "dayjs";
import { checkAndSynchBookingsFiles } from "@/utils/vue-helpers/pos-files";
import { SyncType } from "@/interfaces/store/IBooking";
import { dexieDB } from "@/services/dexie-database";
import { initiateOrdersSync, MAX_SYNC_ATTEMPTS } from "@/services/sync-service";
import { createCompositeId } from "@/utils/helpers/app";
import { IPaginate } from "@/types/app";
import Dexie from "dexie";
import {
  isBookingInDexie,
  shouldSkipBookingPost,
} from "@/utils/helpers/booking";
import Timeout from "await-timeout";

export async function saveUntypedBooking(
  booking: IProductSlotBooking,
  options?: { alreadyPosted?: boolean }
) {
  // lets first do a check to see if this booking is empty or not
  // if the booking is empty then we don't insert it, we log and we notify to Slack because this is an issue
  // Inserting it will cause a synching issue and will block other bookings from synching
  if (booking.pssList && !booking.pssList.length) {
    LogService.info(
      `Error: Empty booking captured, uuid:${booking.uuid}`,
      booking,
      LogLevel.Error
    );
    return;
  }

  const creationTime = dayjs().toISOString();
  const bookingCopy = cloneDeep(booking);
  const untypedBooking: IUntypedBooking = {
    _id: String(bookingCopy.uuid),
    uuid: String(bookingCopy.uuid),
    creationDateTime: creationTime,
    booking: bookingCopy,
    syncStatus: SyncStatus.Pending,
    attempts: 0,
  };
  const { skipPost, syncStatus } = await shouldSkipBookingPost(
    bookingCopy.uuid
  );
  if (
    options?.alreadyPosted ||
    (skipPost && syncStatus === SyncStatus.Synced)
  ) {
    // Booking already posted explicitly
    // OR
    // Booking already posted via sync job, mark it as synced
    untypedBooking.syncStatus = SyncStatus.Synced;
  } else if (skipPost && syncStatus === SyncStatus.Failed) {
    // Booking failed to sync and reached max attempts, since this is an update to the booking object, restore its status to Pending to allow retry
    untypedBooking.syncStatus = SyncStatus.Pending;
  }
  await upsertUntypedBooking(untypedBooking);
  // Trigger the sync
  initiateOrdersSync();
  return true;
}

/**
 * Used to POST promocode booking in a Pending state,
 * throws an error if not a promocode booking, encountring a network error or if backend returns an unexpected response
 * @param booking
 */
export async function postPendingBooking(booking: IProductSlotBooking) {
  if (booking.pssList && !booking.pssList.length) {
    LogService.info(
      `Error: Empty booking captured, uuid:${booking.uuid}`,
      booking,
      LogLevel.Error
    );
    return;
  }

  const bookingCopy = cloneDeep(booking);

  const accountings = bookingCopy.accountings;
  const promocodePaymentMethods = [
    PaymentMethods.POS_VOUCHER,
    PaymentMethods.POS_GIFT_CARD,
    PaymentMethods.POS_PROMO_CODE,
    PaymentMethods.POS_MEMBERSHIP,
  ];
  const hasPromocodeApplied: boolean = accountings.some(
    (acc) =>
      promocodePaymentMethods.findIndex((it) => it === acc.paymentMethod) >=
        0 &&
      acc.state !== AccountingState.Failed &&
      acc.state !== AccountingState.Cancelled
  );
  if (!hasPromocodeApplied) {
    throw new Error("Booking has no promocode applied, save and sync aborted");
  }

  LogService.info(
    `Explicitly posting promocode booking before inserting it into local db, uuid ${booking.uuid}`,
    {
      params: { booking: bookingCopy },
    }
  );
  const start = Date.now();
  try {
    const res = await pushSyncBooking([bookingCopy]);
    const latency = Date.now() - start;
    LogService.info(
      `Promocode: POST Request for /synchronize-bookings took ${latency}ms , uuid: ${booking.uuid}`
    );

    if (res.status === "success") {
      LogService.info(
        `Checking if there are files to synch for booking: ${bookingCopy.uuid}`
      );
      await checkAndSynchBookingsFiles([bookingCopy]);
    } else {
      LogService.info(
        "Invalid backend response when posting promocode booking",
        { res }
      );
      throw new Error(
        "Invalid backend response when posting promocode booking"
      );
    }
  } catch (err: any) {
    const latency = Date.now() - start;
    LogService.info(
      `Promocode: POST Request for /synchronize-bookings failed after ${latency}ms , uuid: ${booking.uuid}`,
      { error: err },
      LogLevel.Error
    );
    LogService.info(
      `Error when explicitly synching attached booking before inserting it into local db, uuid:${booking.uuid}`,
      { params: { err: err } },
      LogLevel.Error
    );
    throw err;
  }

  return true;
}

export async function syncUntypedBookings(untypedBookings: IUntypedBooking[]) {
  // Check if database is open before proceeding
  if (!dexieDB.isOpen()) {
    LogService.info("Database is not open, skipping sync");
    return;
  }

  // We only synch completed booking here that have just been stored in the indexedDB
  // We don't synch pending bookings (e.g: bookings with promocode) or bookings that were pulled from backend (e.g: Edit Order) using this handler,
  // that type of bookings is synched in Booking.vue explicitly before payment processing

  const groupInfo =
    SharedCallbacks.getGroupInfo && SharedCallbacks.getGroupInfo();

  if (!groupInfo || !groupInfo.id) {
    throw "Can't sync bookings without group info";
  }

  // so lets remove any booking that has an applied promocode or that was pulled from backend from bookings array
  const bookings: IProductSlotBooking[] = untypedBookings.map((b) => b.booking);
  const bookingsToPost: IProductSlotBooking[] = [];
  for (let i = 0; i < bookings.length; i++) {
    // Bookings with promocode applied are already patched to backend, we ignore them
    // Bookings that have been pulled from backend and edited here we'll Patch them
    // other bookings are created here, we will POST them    // Any booking that is lready posted to backend, or coming from backend will not be POSTED again

    // ! We can remove the below once we confirm all devices have fully migrated to Dexie
    const hasId = !!bookings[i].id;
    if (hasId || bookings[i].isSynchedManually) {
      LogService.info(
        `Booking with uuid: ${bookings[i].uuid} is already POSTed, it will be handled by PUT`
      );
      // skip this booking
      continue;
    }

    const { skipPost } = await shouldSkipBookingPost(bookings[i].uuid);
    if (!skipPost) {
      // No skip post for this booking, add it to the request
      const cpBooking = cloneDeep(bookings[i]);
      for (const pss of cpBooking.pssList) {
        (pss as any).sellerGroupId = groupInfo.id;
      }
      bookingsToPost.push(cpBooking);
    }
  }

  if (!bookingsToPost.length) {
    LogService.info(`No Booking to be POSTed`);
    return;
  }

  // POST bookings
  LogService.info(
    `(compressed) Doing POST synchronize-bookings of completed booking with uuids: ${bookingsToPost
      .map((b) => b.uuid)
      .join(" ")}`,
    { params: bookingsToPost }
  );
  try {
    const res = await pushSyncBooking(bookingsToPost);
    // if backend responds that booking is successfully synced, we remove it from the list of pendingSyncBookings
    if (res.status === "success") {
      LogService.info(
        `Sync: Successful POST synchronize-bookings, uuids: ${bookingsToPost
          .map((b) => b.uuid)
          .join(",")}`
      );
      BookingModule.removeSyncedBookingFromPendingSyncBookings({
        synchedBooking: res.data.createdBookings,
        typeToDelete: SyncType.POST,
      });

      // Mark the bookings as synced
      const syncedBookings = untypedBookings.filter((b) =>
        (res.data?.createdBookings || []).includes(b.uuid)
      );
      for (const synchedUnTypedBooking of syncedBookings) {
        synchedUnTypedBooking.syncStatus = SyncStatus.Synced;
        await upsertUntypedBooking(synchedUnTypedBooking);
      }

      // If bookings synched has custom fields files in them, then we need to insert the files IDs into their DB
      // to trigger their synching process
      LogService.info(
        `Checking if there are files to synch for bookings: ${bookingsToPost.map(
          (b) => b.uuid
        )}`
      );
      await checkAndSynchBookingsFiles(bookingsToPost);
    } else {
      LogService.info(
        `Sync: Invalid backend response when posting bookings: ${bookingsToPost.map(
          (b) => b.uuid
        )}`,
        { res },
        LogLevel.Error
      );
    }
  } catch (error: any) {
    // If we have a network error then we do nothing, we do not increase attempts
    if (
      error?.networkError?.length ||
      String(error?.message).toLowerCase().includes("network error")
    ) {
      // Do nothing since this is a network error
      LogService.info(
        `Network Error when POSTing bookings: ${bookingsToPost.map(
          (b) => b.uuid
        )}`
      );

      // Mark the bookings as failed without increasing attempts
      for (const untypedBooking of untypedBookings) {
        const booking = bookingsToPost.find(
          (b) => b.uuid === untypedBooking.uuid
        );
        if (!booking) {
          continue;
        }
        untypedBooking.syncStatus = SyncStatus.Failed;
        await upsertUntypedBooking(untypedBooking);
      }
    } else {
      // If error is coming from backend logic (i.e: data we posted are correct) then log it and don't try again
      // otherwise, throw the error so that it tries synching again
      const data = error && error?.response && error?.response.data;
      const msg =
        data?.messages && data?.messages?.length ? data.messages[0] : "";

      if (msg.includes("SMEETZPOS")) {
        // log the error and don't try again
        LogService.info(
          `SMEETZPOS Error when POST booking with uuid ${bookingsToPost
            .map((b) => b.uuid)
            .join(" - ")}`,
          { params: { errorMessage: msg } },
          LogLevel.Error
        );
        if (bookingsToPost.length === 1) {
          // this is a booking with SMEETZPOS error, let's ignore it
          BookingModule.removeSyncedBookingFromPendingSyncBookings({
            synchedBooking: [bookingsToPost[0].uuid as string],
            typeToDelete: SyncType.POST,
          });
          // Mark the booking as ignored
          const untypedBooking = untypedBookings.find(
            (b) => b.uuid === bookingsToPost[0].uuid
          );
          if (untypedBooking) {
            untypedBooking.syncStatus = SyncStatus.Ignored;
            untypedBooking.attempts = (untypedBooking.attempts || 0) + 1;
            await upsertUntypedBooking(untypedBooking);
          }
        }
      } else {
        LogService.info(
          `Error when POST booking with uuid ${bookingsToPost
            .map((b) => b.uuid)
            .join(" - ")}`,
          { params: { error: error } },
          LogLevel.Error
        );
        // Mark the bookings as failed (it will try again)
        for (const untypedBooking of untypedBookings) {
          const booking = bookingsToPost.find(
            (b) => b.uuid === untypedBooking.uuid
          );
          if (!booking) {
            continue;
          }
          untypedBooking.syncStatus = SyncStatus.Failed;
          untypedBooking.attempts = (untypedBooking.attempts || 0) + 1;
          await upsertUntypedBooking(untypedBooking);
        }
      }
    }
  }
}

/**
 * Save or Update untyped booking to local db
 * @param untypedBooking Untyped booking to save.
 */
export async function upsertUntypedBooking(untypedBooking: IUntypedBooking) {
  LogService.info(
    `(compressed) Storing untyped booking into local database, uuid:${untypedBooking.uuid}, syncStatus:${untypedBooking.syncStatus}`,
    untypedBooking
  );
  try {
    await dexieDB.untypedBookings.put(untypedBooking);
  } catch (err: any) {
    LogService.info(
      `Error when storing untyped booking into local db, uuid:${untypedBooking.uuid}, syncStatus:${untypedBooking.syncStatus}`,
      { params: { booking: untypedBooking, err: err } },
      LogLevel.Error
    );
    throw err;
  }
}

/**
 * Sync untyped bookings from Dexie to backend
 * Priority is given to new bookings over failed ones
 * @param limit
 */
export async function syncDexieBookings(limit = 10): Promise<void> {
  let syncedCount = 0;

  while (syncedCount < limit) {
    // Check if database is still open before each iteration
    if (!dexieDB.isOpen()) {
      LogService.info("Database connection closed during sync, stopping");
      break;
    }

    let bookingToSync: IUntypedBooking | undefined;

    try {
      // Try to get one Pending booking

      // Use syncStatus+CreationDateTime index to query the newest pending booking using an index for better performance
      // Use Dexie.minKey and Dexie.maxKey
      // Dexie.minKey: Represents the lowest possible value for a key in IndexedDB. It is used as the lower bound in a range query, meaning "start from the very beginning."
      // Dexie.maxKey: Represents the highest possible value for a key in IndexedDB. It is used as the upper bound in a range query, meaning "go up to the very end."
      // Get all records where syncStatus is Pending and creationDateTime is between the smallest and largest possible values (i.e., all possible creationDateTime values for pending bookings).
      const newestPending = await dexieDB.untypedBookings
        .where("[syncStatus+creationDateTime]")
        .between(
          [SyncStatus.Pending, Dexie.minKey],
          [SyncStatus.Pending, Dexie.maxKey]
        )
        .reverse() // newest first
        .first();

      if (newestPending) {
        LogService.info(
          `Sync: Found Pending booking to sync, uuid: ${newestPending.uuid}`
        );
        bookingToSync = newestPending;
      } else {
        // No Pending bookings, try to get one Failed (ordered by attempts)
        const failed = await dexieDB.untypedBookings
          .where("[syncStatus+attempts]")
          .between(
            [SyncStatus.Failed, 0],
            [SyncStatus.Failed, MAX_SYNC_ATTEMPTS]
          )
          .first();

        if (failed) {
          LogService.info(
            `Sync: Found Failed booking to sync, uuid: ${failed.uuid}, attempts: ${failed.attempts}`
          );
          bookingToSync = failed;
        }
      }
    } catch (error) {
      LogService.info(
        `Sync: Error when fetching untyped booking from Dexie db`,
        error,
        LogLevel.Error
      );
      break;
    }

    // No more untyped bookings to sync
    if (!bookingToSync) {
      LogService.info("Sync: No more untyped bookings to sync");
      break;
    }

    // Mark as "InProgress" before we start the sync
    try {
      bookingToSync.syncStatus = SyncStatus.InProgress;
      await upsertUntypedBooking(bookingToSync);
      LogService.info(
        `Sync: Marked booking ${bookingToSync.uuid} as InProgress`
      );
    } catch (error) {
      LogService.info(
        `Sync: Error marking booking ${bookingToSync.uuid} as InProgress`,
        error,
        LogLevel.Error
      );
    }

    // Sync the booking
    try {
      LogService.info(
        `Sync: Going to try sync booking with uuid: ${
          bookingToSync.uuid
        }, iteration: ${syncedCount + 1}`
      );
      await syncUntypedBookings([bookingToSync]);
      LogService.info(
        `Sync: Done syncing booking with uuid: ${
          bookingToSync.uuid
        }, iteration: ${syncedCount + 1}`
      );
    } catch (error) {
      LogService.info(
        `Sync: Error syncing booking with uuid: ${
          bookingToSync.uuid
        }, iteration: ${syncedCount + 1}`,
        error,
        LogLevel.Error
      );
    }
    syncedCount++;
    await Timeout.set(1000);
  }
}

export async function getBookingsByCashfloatUuid(
  cashfloatUuid: string,
  pagination?: IPaginate
): Promise<IProductSlotBooking[]> {
  let query = dexieDB.untypedBookings
    .where("booking.cashfloatUuid")
    .equals(cashfloatUuid);

  if (pagination) {
    const { skip, limit } = pagination;
    query = query.offset(skip).limit(limit);
  }

  const untypedBookings = await query.toArray();
  return untypedBookings.map((ub) => ub.booking);
}
